import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SEO from '../SEO';
import RateLimitNotice from '../ui/RateLimitNotice';
import LandingNav from '../Layout/LandingNav';
import LandingFooter from '../Layout/LandingFooter';
import GoogleButton from './GoogleButton';

function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showRateLimit, setShowRateLimit] = useState(false);
  const { login, googleLogin, isLoading, error, clearError } = useAuth();
  const { theme } = useTheme();
  const d = theme === 'dark';

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      clearError();
      setShowRateLimit(false);
      await login(data.email, data.password);
      toast.success('Welcome back!');
    } catch (err) {
      if (err.isRateLimit || err.status === 429 || err.message.includes('Too many')) {
        setShowRateLimit(true);
        return;
      }

      if (err.message.includes('email')) {
        setError('email', { message: err.message });
      } else if (err.message.includes('password')) {
        setError('password', { message: err.message });
      } else {
        toast.error(err.message || 'Login failed');
      }
    }
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Log in"
        description="Sign in to your PaperBrainz dashboard."
        path="/login"
        noindex
      />
      <LandingNav />

      <div className="flex-1 flex items-center justify-center px-4 pt-44 pb-32">
        <div className="relative z-10 w-full max-w-md space-y-8">
          {/* Card */}
          <div className="">
            {/* Header */}
            <div className="text-center mb-10">
              <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                Sign in to PaperBrainz
              </h2>
              <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                AI-Powered Customer Service Dashboard
              </p>
            </div>

            {/* Google Sign-In */}
            <GoogleButton d={d} text="Sign in with Google" onSuccess={async (credential) => {
              try {
                clearError();
                await googleLogin(credential);
                toast.success('Welcome!');
              } catch (err) {
                toast.error(err.message || 'Google sign-in failed');
              }
            }} />

            {/* Divider */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`flex-1 h-px ${d ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
              <span className={`text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>or</span>
              <div className={`flex-1 h-px ${d ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
            </div>

            {/* Form */}
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {showRateLimit && (
                <RateLimitNotice onClose={() => setShowRateLimit(false)} />
              )}

              {error && !showRateLimit && (
                <div className={`rounded-[12px] border p-3.5 ${d ? 'bg-red-500/[0.08] border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm ${d ? 'text-red-400' : 'text-red-600'}`}>{error}</p>
                </div>
              )}

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                  </div>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address',
                      },
                    })}
                    type="email"
                    autoComplete="email"
                    className={`
                      block w-full pl-10 pr-3 py-3 rounded-[12px] border text-sm transition-colors
                      ${errors.email
                        ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
                        : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
                      }
                      ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
                      focus:outline-none focus:ring-1 ${errors.email ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
                    `}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                  </div>
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={`
                      block w-full pl-10 pr-12 py-3 rounded-[12px] border text-sm transition-colors
                      ${errors.password
                        ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
                        : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
                      }
                      ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
                      focus:outline-none focus:ring-1 ${errors.password ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
                    `}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                    ) : (
                      <Eye className={`h-4.5 w-4.5 ${d ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.password.message}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className={`
                  w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                  ${isLoading
                    ? d ? 'bg-white/10 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                  }
                `}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Sign in'
                )}
              </button>

            </form>
          </div>

          {/* Create account card */}
          <div className={`rounded-[16px] border p-5 flex items-center justify-between transition-colors duration-300 ${d ? 'bg-[#111] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div>
              <p className={`text-sm font-medium ${d ? 'text-white' : 'text-gray-900'}`}>Don't have an account?</p>
              <p className={`text-xs mt-0.5 ${d ? 'text-gray-500' : 'text-gray-500'}`}>Create one and get started for free.</p>
            </div>
            <Link
              to="/register"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                d ? 'border-white/[0.08] text-blue-400 hover:bg-white/[0.04]' : 'border-gray-200 text-blue-600 hover:bg-gray-50'
              }`}
            >
              Sign up <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>

          {/* Forgot password card */}
          <Link
            to="/forgot-password"
            className={`block rounded-[16px] border p-4 text-center text-sm transition-colors duration-300 ${
              d ? 'bg-[#111] border-white/[0.06] text-gray-400 hover:text-gray-300' : 'bg-white border-gray-200 shadow-sm text-gray-500 hover:text-gray-700'
            }`}
          >
            Recover password
          </Link>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default LoginPage;
